import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useColumnDragSave from "../../utils/hooks/useColumnDragSave";
import { DLabEquipmentList as CommonAgGrid, getFieldValue } from "@digitallab/grid-common-frontend-components";
import { CloudSearchConfig } from "../../components/shared/CloudSearchConfig";
import { OpenSearchConfig } from "../../components/shared/OpenSearchConfig";
import EquipmentActionsMenu from "./EquipmentActionsMenu";
import {
  agGridAutoGroupfieldName,
  allTableColumn,
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  DEFAULT_PAGE_SIZE,
  DUMMY_ROW,
  entryType,
  listOfFieldAcceptOnlyInteger,
  listOfFieldsIgnoredForSearch,
  listOfNumberFilter,
  literalFields
} from "../../constants";
import { filter, find, keyBy, sortBy, uniq } from "lodash";
import { convertStrToObjColumns, convertToCamelCase, convertToSnakeCase, getEnv } from "../../utils/helpers/text";
import { useContainerHeight } from "../../utils/hooks";
import { getLPMyLabFilter } from "./LoadEquipmentService";
import {
  getColumnObj,
  onCreatefilter,
  onCreateSelectColumn,
  onDeleteSaveConfigurations,
  onUpdateFilter,
  onUpdateSelectColumn,
  saveLastPageSize,
  buildDisplayDefinitionFromColumns,
  normalizeBooleanFilters,
  rehydrateStoredColumn,
  OPENSEARCH_CONFIGURATION
} from "./EquipmentHelper";
import { filterKeyMapping, TOOL_PANELS } from "../../components/DLabGrid/dLabGridConstant";
import DATA_MODEL_TABLE from "../../utils/constants/dataModelTable";
import { useSelector } from "react-redux";
import { withApollo } from "react-apollo";
import {
  CREATE_USER_DISPLAY_COLUMN,
  CREATE_USER_FILTER,
  DELETE_USER_DISPLAY_COLUMN,
  DELETE_USER_FILTER,
  UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
  UPDATE_USER_DISPLAY_COLUMN,
  UPDATE_USER_FILTER
} from "../../gql/logBooksapi";
import { OwcTypography } from "@one/react";
import EquipmentActionPage from "./EquipmentActionPage";
import { CoverSheetMainPageContext } from "../cover-sheet/cover-sheet-main-page-context/context";

const isEquipmentModelKey = (key) =>
  filterKeyMapping[key] === DATA_MODEL_TABLE?.equipmentModel?.key ||
  key === DATA_MODEL_TABLE?.equipmentModel?.key;

const getSetFilterValues = (key, stateValue, currentState) => {
  const hasAutoGroup = Object.keys(currentState).includes(agGridAutoGroupfieldName);
  if (isEquipmentModelKey(key) && hasAutoGroup) {
    return uniq([...stateValue.values, ...currentState[agGridAutoGroupfieldName].values]);
  }
  return stateValue.values;
};

const resolveFilterValue = (key, currentState) => {
  const snakeKey = convertToSnakeCase(filterKeyMapping[key] || key);
  const stateValue = currentState[snakeKey];
  if (stateValue === undefined) {
    return DEFAULT_FILTER.filterDefinition[key];
  }
  if (stateValue?.filterType === "set") {
    return getSetFilterValues(key, stateValue, currentState);
  }
  return stateValue;
};

export const buildFilterStateFromAgGrid = (currentState) => {
  const obj = {};
  for (const key of Object.keys(DEFAULT_FILTER.filterDefinition)) {
    obj[key] = resolveFilterValue(key, currentState);
  }
  return obj;
};

const currentEnv = getEnv();
const EquipmentMainPage = ({
  masterDataFilters,
  myLabFilter,
  setMyLabFilter,
  lastFavFilter,
  setLastFavFilter,
  userFilters,
  setUserFilters,
  userDisplayColumn,
  setUserDisplayColumn,
  lastDisplayColumns,
  setLastDisplayColumns,
  lastPageSize,
  setLastPageSize,
  client,
  landingPageAppSyncClient
}) => {
  const [dataModel, setDataModel] = useState({});
  const [additionalFields, setAdditionalFields] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [defaultShowColumns, setDefaultShowColumns] = useState({
    ...DEFAULT_COLUMNS()
  });
  const containerHeight = useContainerHeight(currentEnv, 195);
  const [openDialog, setOpenDialog] = useState(false);
  const storeUser = useSelector((store) => store.user);
  const { logBookEquipment, logType } = useContext(CoverSheetMainPageContext);
  const localUserFilters = useRef(userFilters);
  const autoGroupColField = "equipment_model";

  useEffect(() => {
    setAttributes(() => attr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerHeight]);
  useEffect(() => {
    localUserFilters.current = userFilters;
  }, [userFilters]);

  useEffect(() => {
    const unorderedHiddenColumn = [];
    const orderedShowColumns = sortBy(
      convertStrToObjColumns(lastDisplayColumns)?.displayDefinition?.showColumns,
      ["order"],
      ["asc"]
    );

    allTableColumn()?.forEach((value) => {
      const obj = find(orderedShowColumns, {
        key: value?.key
      });
      if (!obj) {
        unorderedHiddenColumn.push(value);
      }
    });

    // A stored entry is a persistence record, not a column definition — see rehydrateStoredColumn.
    const schemaColumns = keyBy(allTableColumn(), "key");

    const redefinedColumns = [...orderedShowColumns, ...unorderedHiddenColumn]?.map((value) => {
      return getColumnObj(
        rehydrateStoredColumn(schemaColumns, value),
        masterDataFilters,
        convertToCamelCase(autoGroupColField)
      );
    });

    const defaultShowCols = filter(allTableColumn(), { show: true });
    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, masterDataFilters, convertToCamelCase(autoGroupColField));
      });
    });

    const aditionColumns = [
      {
        field: "actions",
        headerName: "",
        maxWidth: 50,
        filter: false,
        pinned: "right",
        lockVisible: true,
        lockPosition: "right",
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
        cellClass: "action-render",
        cellRenderer: actionRender
      }
    ];
    setAdditionalFields(() => [...aditionColumns]);
    setDataModel([...redefinedColumns]);
    // eslint-disable-next-line
  }, [masterDataFilters, lastDisplayColumns]);

  let attr = {
    height: containerHeight,
    rowModelType: "serverSide",
    rowQuickSearch: false,
    treeData: true,
    suppressPaginationPanel: true,
    pagination: true,
    animateRows: true,
    rowSelection: "multiple",
    rowMultiSelectWithClick: true,
    rowExport: false,
    masterDetail: true,
    isGroupOpenByDefault: false,
    defaultToolPanel: "filters",
    hiddenByDefault: true,
    serverSideInfiniteScroll: false,
    serverSideSortOnServer: true,
    suppressRowClickSelection: true
  };

  const requestServer = (cloudSearchConfigOption) => {
    if (!OPENSEARCH_CONFIGURATION && cloudSearchConfigOption?.queryOptions) {

      cloudSearchConfigOption.queryOptions = cloudSearchConfigOption.queryOptions.replace(
        "'booking_access_control',",
        ""
      );
      cloudSearchConfigOption.queryOptions = cloudSearchConfigOption.queryOptions.replace("'is_hazardous',", "");
      cloudSearchConfigOption.queryOptions = cloudSearchConfigOption.queryOptions.replace("'should_assisted',", "");
    }

    // The int-boolean columns are filtered as Yes/No but stored as 1/0 — see normalizeBooleanFilters.
    cloudSearchConfigOption.searchValue = normalizeBooleanFilters(cloudSearchConfigOption?.searchValue);
    cloudSearchConfigOption.filterQuery = normalizeBooleanFilters(cloudSearchConfigOption?.filterQuery);

    if (OPENSEARCH_CONFIGURATION) {
      return OpenSearchConfig(cloudSearchConfigOption);
    } else {
      return CloudSearchConfig(cloudSearchConfigOption);
    }
  };

  const entryTypeCheck = (dataItem) => {
    if (OPENSEARCH_CONFIGURATION) {
      return getFieldValue(dataItem, "entry_type") === entryType?.cluster;
    } else {
      return dataItem?.entry_type?.includes(entryType?.cluster)
    }
  }
  const isServerSideGroup = useCallback((dataItem) => {
    // indicate if node is a group

    if (dataItem?.entry_type && entryTypeCheck(dataItem)) {
      return dataItem?.id;
    }

    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getServerSideGroupKey = useCallback((dataItem) => {
    // specify which group key to use

    if (dataItem?.entry_type && entryTypeCheck(dataItem)) {
      return dataItem?.id;
    }

    return "";
  }, []);
  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Model",
      field: autoGroupColField,
      sortable: true,
      pinned: "left",
      lockPinned: true,
      filter: "agSetColumnFilter",
      lockVisible: true,
      filterParams: {
        buttons: ["reset"],
        defaultToNothingSelected: true,
        values: (params) => {
          const values = masterDataFilters[DATA_MODEL_TABLE?.equipmentModel?.key] || [];
          params.success(values);
        }
      },
      cellStyle: (params) => {
        return params?.node?.level > 0 &&
          (getFieldValue(params?.data, "entry_type") === entryType?.cluster ||
            getFieldValue(params?.data, "entry_type") === entryType?.clusterSubequipment)
          ? {
            paddingLeft: 20,
            border: "none !important",
            backgroundColor: "#F5F5F2",
            backgroundImage: "conic-gradient(at 7% 90%, #FFFFFF 180deg, #F5F5F2 180deg)"
          }
          : {};
      },
      cellRendererParams: {
        innerRenderer: (params) => {
          return getFieldValue(params?.data, "equipment_model");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMyLabUpdated = async () => {
    const updatedFilter = await getLPMyLabFilter(client);
    setMyLabFilter(updatedFilter);
  };

  const actionRender = (params) => {
    return getFieldValue(params?.data, "id") === DUMMY_ROW ? (
      ""
    ) : (
      <EquipmentActionsMenu
        params={params}
        landingPageAppSyncClient={landingPageAppSyncClient}
        myLabFilter={myLabFilter}
        onMyLabUpdated={handleMyLabUpdated}
      />
    );
  };
  const onSave = async ({
    currentState,
    chipName = "",
    type = "update",
    isAgGridObj = false,
    pannel = "",
    isSaveConfigUpdated = false,
    updateRedux = true
  }) => {
    let result = null;
    if (pannel === TOOL_PANELS?.filters) {
      const obj = isAgGridObj ? buildFilterStateFromAgGrid(currentState) : { ...currentState };

      const lastSavedFilter = {
        filterDefinition: obj,
        filterName: chipName
      };
      if (type === "create") {
        lastSavedFilter.userID = storeUser?.id;
        lastSavedFilter.chipName = chipName;
        result = await onCreatefilter({
          id: storeUser?.id,
          email: storeUser?.email,
          userFilters: localUserFilters.current,
          lastFavFilter: lastSavedFilter,
          client,
          query: CREATE_USER_FILTER,
          userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
          updateUserFilters: setUserFilters,
          updateLastFavFilter: setLastFavFilter
        });
      } else {
        result = await onUpdateFilter({
          lastFavFilter: lastSavedFilter,
          isSaveConfigUpdated: isSaveConfigUpdated,
          updateRedux,
          id: storeUser?.id,
          email: storeUser?.email,
          client,
          query: UPDATE_USER_FILTER,
          userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
          updateLastFavFilter: setLastFavFilter
        });
      }
      return { savedObj: lastSavedFilter, result };
    } else if (pannel === TOOL_PANELS?.columns) {
      const { showColumns, hideColumns } = buildDisplayDefinitionFromColumns([...currentState]);
      let savecolumnObj = {
        displayName: chipName,
        displayDefinition: {
          showColumns,
          hideColumns
        }
      };
      if (updateRedux) {
        setLastDisplayColumns(savecolumnObj);
      }
      if (type === "create") {
        savecolumnObj.userID = storeUser?.id;
        savecolumnObj.chipName = chipName;
        result = await onCreateSelectColumn({
          id: storeUser?.id,
          email: storeUser?.email,
          userDisplayList: userDisplayColumn,
          unParsedData: savecolumnObj,
          client,
          query: CREATE_USER_DISPLAY_COLUMN,
          userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
          updateUserDisplayColumns: setUserDisplayColumn,
          updateLastDisplayColumn: setLastDisplayColumns
        });
      } else {
        result = await onUpdateSelectColumn({
          data: JSON.stringify({ equipmentList: savecolumnObj }),
          isSaveConfigUpdated,
          updateRedux: false,
          id: storeUser?.id,
          email: storeUser?.email,
          client,
          userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
          query: UPDATE_USER_DISPLAY_COLUMN,
          userDisplayList: userDisplayColumn,
          updateUserDisplayColumns: setUserDisplayColumn,
          updateLastDisplayColumn: setLastDisplayColumns
        });
      }
      return { savedObj: savecolumnObj, result };
    }
  };

  const { onGridDragStopped } = useColumnDragSave(onSave);

  const onChipDelete = async (chipToDelete, lastSavedChip, panel, userFilters) => {
    return await onDeleteSaveConfigurations({
      userID: storeUser?.id,
      chipToDelete,
      panel,
      lastSavedChip,
      client,
      email: storeUser?.email,
      deleteFilterQuery: DELETE_USER_FILTER,
      deleteColumnQuery: DELETE_USER_DISPLAY_COLUMN,
      userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
      userFilters,
      userDisplayList: userDisplayColumn,
      updateUserFilters: setUserFilters,
      updateUserDisplayColumns: setUserDisplayColumn,
      updateLastFavFilter: setLastFavFilter,
      updateLastDisplayColumn: setLastDisplayColumns
    });
  };

  const onResetAll = async () => {
    await onUpdateFilter({
      lastFavFilter: DEFAULT_FILTER,
      updateRedux: true,
      id: storeUser?.id,
      email: storeUser?.email,
      client,
      query: UPDATE_USER_FILTER,
      userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
      updateLastFavFilter: setLastFavFilter
    });
    await onUpdateSelectColumn({
      data: JSON.stringify({ equipmentList: DEFAULT_COLUMNS() }),
      updateRedux: true,
      id: storeUser?.id,
      email: storeUser?.email,
      client,
      userProfileQuery: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
      query: UPDATE_USER_DISPLAY_COLUMN,
      userDisplayList: userDisplayColumn,
      updateUserDisplayColumns: setUserDisplayColumn,
      updateLastDisplayColumn: setLastDisplayColumns
    });
  };
  const onRowsPerChange = async (rowPerPage) => {
    await saveLastPageSize({
      lastPageSize: rowPerPage,
      query: UPDATE_DIGITAL_LAB_LOGBOOK_USER_PROFILE_PERSONAL_FIELDS,
      updateLastPageSize: setLastPageSize,
      id: storeUser?.id,
      email: storeUser?.email,
      client
    });
  };

  return (
    <>
      <div>
        <OwcTypography variant="title5" style={{ paddingLeft: "16px" }}>
          Equipment list
        </OwcTypography>
        {Object?.keys(attributes)?.length > 0 && (
          <CommonAgGrid
            storeUser={storeUser}
            autoGroupColumnDefObj={autoGroupColumnDef}
            columnData={dataModel}
            masterDataFilters={masterDataFilters}
            additionalFields={additionalFields}
            userFilters={userFilters}
            myLabFilter={myLabFilter}
            lastFavFilter={lastFavFilter}
            lastDisplayColumns={lastDisplayColumns ?? DEFAULT_COLUMNS()}
            userDisplays={userDisplayColumn}
            systemDisplays={[]}
            onSave={onSave}
            onChipDelete={onChipDelete}
            defaultFilterObj={DEFAULT_FILTER}
            defaultShowColumns={defaultShowColumns}
            listOfFieldsIgnoredForSearch={listOfFieldsIgnoredForSearch()}
            listOfNumberFilter={listOfNumberFilter()}
            listOfFieldAcceptOnlyInteger={listOfFieldAcceptOnlyInteger()}
            isServerSideGroupObj={isServerSideGroup}
            getServerSideGroupKeyObj={getServerSideGroupKey}
            selectedEquipment={[]}
            onResetAll={onResetAll}
            showSaveConfiguration={true}
            limit={lastPageSize ?? DEFAULT_PAGE_SIZE}
            onRowsPerChange={onRowsPerChange}
            requestServer={requestServer}
            siteName={storeUser?.site}
            literalFields={literalFields}
            client={landingPageAppSyncClient}
            onDragStopped={onGridDragStopped}
            {...attributes}
          ></CommonAgGrid>
        )}
      </div>
      {logBookEquipment && (
        <>
          <EquipmentActionPage
            setOpenDialog={setOpenDialog}
            openDialog={openDialog}
            logType={logType}
            equipmentDetail={logBookEquipment}
          />
        </>
      )}
    </>
  );
};

// export default EquipmentMainPage;
export default withApollo(EquipmentMainPage);
